import { useCallback, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Loader2, AlertTriangle, CheckCircle2, ArrowDownUp, Eye, Zap, RefreshCw, Database } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, useJobResume, JobErrorBlock } from './JobPollingPanel';

const signCensusJobStatus = (jobId) => api.get(`/admin/diagnostics/transaction-sign-census/job-status/${jobId}`);
const signCensusJobLatest = (operation) => api.get(`/admin/diagnostics/transaction-sign-census/job-latest/${operation}`);

export function TransactionSignCensusPanel() {
  const [loading, setLoading] = useState(false);
  const [census, setCensus] = useState(null);
  const [amountTypesLoading, setAmountTypesLoading] = useState(false);
  const [amountTypes, setAmountTypes] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [applyStarting, setApplyStarting] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const [applyError, setApplyError] = useState(null);

  // Re-sync state
  const [resyncCensusLoading, setResyncCensusLoading] = useState(false);
  const [resyncCensus, setResyncCensus] = useState(null);
  const [resyncPreviewLoading, setResyncPreviewLoading] = useState(false);
  const [resyncPreview, setResyncPreview] = useState(null);
  const [resyncApplyStarting, setResyncApplyStarting] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [resyncError, setResyncError] = useState(null);

  // Bridge re-sync state
  const [bridgePreviewLoading, setBridgePreviewLoading] = useState(false);
  const [bridgePreview, setBridgePreview] = useState(null);
  const [bridgeApplyStarting, setBridgeApplyStarting] = useState(false);
  const [bridgeResult, setBridgeResult] = useState(null);
  const [bridgeError, setBridgeError] = useState(null);

  // OB re-sync state
  const [obPreviewLoading, setObPreviewLoading] = useState(false);
  const [obPreview, setObPreview] = useState(null);
  const [obApplyStarting, setObApplyStarting] = useState(false);
  const [obResult, setObResult] = useState(null);
  const [obError, setObError] = useState(null);

  // Each Apply below used to fetch from the provider (Enable Banking /
  // Bridge / TrueLayer) and write every matched transaction synchronously
  // in-request -- the class of bug this slice moves out-of-band (CLAUDE.md
  // scale-boxed operations). One shared job-status/job-latest pair on the
  // backend (services/chunked_chain.py JobStore, keyed by operation), one
  // shared polling hook here.
  const applyPoll = useJobPolling({
    fetchStatus: signCensusJobStatus,
    onCompleted: useCallback((result) => {
      setApplyResult(result);
      setApplyError(null);
      if (result.total_flipped > 0) {
        toast.success(`Repaired ${result.total_flipped} transaction(s) across ${result.accounts_affected} account(s)`);
      } else {
        toast.info('No changes (0 flipped)');
      }
    }, []),
    onFailed: useCallback((error) => { setApplyError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => signCensusJobLatest('backfill_apply'), poll: applyPoll });

  const resyncApplyPoll = useJobPolling({
    fetchStatus: signCensusJobStatus,
    onCompleted: useCallback((result) => {
      setResyncResult(result);
      setResyncError(null);
      if (result.total_resynced > 0) {
        toast.success(`Re-synced ${result.total_resynced} transaction(s), ${result.total_sign_flipped} sign(s) changed`);
      } else {
        toast.info(result.message || 'No transactions re-synced');
      }
    }, []),
    onFailed: useCallback((error) => { setResyncError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => signCensusJobLatest('eb_resync_apply'), poll: resyncApplyPoll });

  const bridgeApplyPoll = useJobPolling({
    fetchStatus: signCensusJobStatus,
    onCompleted: useCallback((result) => {
      setBridgeResult(result);
      setBridgeError(null);
      if (result.total_sign_changed > 0) {
        toast.success(`Bridge re-sync: ${result.total_sign_changed} sign(s) changed`);
      } else {
        toast.info(result.message || `Bridge re-sync: ${result.total_resynced} resynced, 0 sign changes (accounts confirmed credit-only)`);
      }
    }, []),
    onFailed: useCallback((error) => { setBridgeError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => signCensusJobLatest('bridge_resync_apply'), poll: bridgeApplyPoll });

  const obApplyPoll = useJobPolling({
    fetchStatus: signCensusJobStatus,
    onCompleted: useCallback((result) => {
      setObResult(result);
      setObError(null);
      if (result.total_sign_changed > 0) {
        toast.success(`OB re-sync: ${result.total_sign_changed} sign(s) changed`);
      } else {
        toast.info(result.message || `OB re-sync: ${result.total_resynced} resynced, 0 sign changes`);
      }
    }, []),
    onFailed: useCallback((error) => { setObError(error); toast.error(error); }, []),
  });
  useJobResume({ fetchLatest: () => signCensusJobLatest('ob_resync_apply'), poll: obApplyPoll });

  const applying = applyStarting || applyPoll.polling;
  const resyncApplying = resyncApplyStarting || resyncApplyPoll.polling;
  const bridgeApplying = bridgeApplyStarting || bridgeApplyPoll.polling;
  const obApplying = obApplyStarting || obApplyPoll.polling;

  const runCensus = async () => {
    setLoading(true);
    setCensus(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-census');
      setCensus(data);
      if (data.debit_shaped_positive_total === 0) {
        toast.success('No debit-shaped-but-positive transactions found');
      } else {
        toast.warning(`${data.debit_shaped_positive_total} debit-shaped-but-positive transaction(s)`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const runPreview = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setApplyResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-backfill/preview');
      setPreview(data);
      if (data.total_to_flip === 0) {
        toast.success('No transactions need sign repair');
      } else {
        toast.info(`${data.total_to_flip} transaction(s) would be flipped`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPreviewLoading(false);
    }
  };

  const runApply = async () => {
    if (!window.confirm(
      `Apply sign repair? This will negate ${preview?.total_to_flip ?? '?'} ` +
      `DBIT transactions that are stored positive. Runs in the background -- ` +
      `safe to navigate away and come back.`
    )) return;
    setApplyStarting(true);
    setApplyResult(null);
    setApplyError(null);
    try {
      const { data } = await api.post('/admin/diagnostics/transaction-sign-backfill/apply/start');
      setPreview(null);
      if (data.job_id) applyPoll.start(data.job_id);
    } catch (err) {
      setApplyError(getErrorMessage(err));
    } finally {
      setApplyStarting(false);
    }
  };

  // Re-sync handlers
  const runResyncCensus = async () => {
    setResyncCensusLoading(true);
    setResyncCensus(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-resync/census');
      setResyncCensus(data);
      if (data.total_no_indicator === 0) {
        toast.success('No transactions without indicator');
      } else {
        toast.info(`${data.total_no_indicator} transaction(s) lack indicator`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResyncCensusLoading(false);
    }
  };

  const runResyncPreview = async () => {
    setResyncPreviewLoading(true);
    setResyncPreview(null);
    setResyncResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-resync/preview');
      setResyncPreview(data);
      if (data.total_resyncable_estimate === 0) {
        toast.info('No transactions within PSD2 window to re-sync');
      } else {
        toast.info(`~${data.total_resyncable_estimate} re-syncable from bank`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setResyncPreviewLoading(false);
    }
  };

  const runResyncApply = async () => {
    const est = resyncPreview?.total_resyncable_estimate ?? '?';
    if (!window.confirm(
      `Apply re-sync? This will re-fetch ~${est} transaction(s) from ` +
      `Enable Banking and update stored amounts from the fresh payload. ` +
      `Runs in the background -- safe to navigate away and come back.`
    )) return;
    setResyncApplyStarting(true);
    setResyncResult(null);
    setResyncError(null);
    try {
      const { data } = await api.post('/admin/diagnostics/transaction-sign-resync/apply/start');
      setResyncPreview(null);
      if (data.job_id) resyncApplyPoll.start(data.job_id);
    } catch (err) {
      setResyncError(getErrorMessage(err));
    } finally {
      setResyncApplyStarting(false);
    }
  };

  // Bridge re-sync handlers
  const runBridgePreview = async () => {
    setBridgePreviewLoading(true);
    setBridgePreview(null);
    setBridgeResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-resync/bridge/preview');
      setBridgePreview(data);
      if (data.total_not_resynced === 0) {
        toast.success('All Bridge transactions already resynced');
      } else {
        toast.info(`${data.total_not_resynced} Bridge transaction(s) to re-sync`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBridgePreviewLoading(false);
    }
  };

  const runBridgeApply = async () => {
    if (!window.confirm(
      `Apply Bridge re-sync? This will re-fetch ${bridgePreview?.total_not_resynced ?? '?'} ` +
      `transaction(s) from the Bridge API and update stored amounts. ` +
      `Runs in the background -- safe to navigate away and come back.`
    )) return;
    setBridgeApplyStarting(true);
    setBridgeResult(null);
    setBridgeError(null);
    try {
      const { data } = await api.post('/admin/diagnostics/transaction-sign-resync/bridge/apply/start');
      setBridgePreview(null);
      if (data.job_id) bridgeApplyPoll.start(data.job_id);
    } catch (err) {
      setBridgeError(getErrorMessage(err));
    } finally {
      setBridgeApplyStarting(false);
    }
  };

  const runAmountTypes = async () => {
    setAmountTypesLoading(true);
    setAmountTypes(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-amount-types');
      setAmountTypes(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setAmountTypesLoading(false);
    }
  };

  // OB re-sync handlers
  const runObPreview = async () => {
    setObPreviewLoading(true);
    setObPreview(null);
    setObResult(null);
    try {
      const { data } = await api.get('/admin/diagnostics/transaction-sign-resync/openbanking/preview');
      setObPreview(data);
      if (data.total_without_type === 0) {
        toast.success('All OB transactions have transaction_type');
      } else {
        toast.info(`${data.total_without_type} OB transaction(s) without type`);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setObPreviewLoading(false);
    }
  };

  const runObApply = async () => {
    if (!window.confirm(
      `Apply OB re-sync? This will re-fetch ${obPreview?.total_without_type ?? '?'} ` +
      `transaction(s) from TrueLayer and update stored amounts + transaction_type. ` +
      `Runs in the background -- safe to navigate away and come back.`
    )) return;
    setObApplyStarting(true);
    setObResult(null);
    setObError(null);
    try {
      const { data } = await api.post('/admin/diagnostics/transaction-sign-resync/openbanking/apply/start');
      setObPreview(null);
      if (data.job_id) obApplyPoll.start(data.job_id);
    } catch (err) {
      setObError(getErrorMessage(err));
    } finally {
      setObApplyStarting(false);
    }
  };

  const fmt = (n) => {
    if (n == null) return '';
    const v = typeof n === 'number' ? n : Number(n);
    return isNaN(v) ? String(n) : v.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <Card data-testid="transaction-sign-census-panel">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ArrowDownUp className="h-5 w-5" />
          Transaction Sign Census
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Detect transactions whose amount sign is wrong (debit-shaped descriptions stored
          as positive). Census reads prod data; backfill repairs Enable Banking DBIT transactions.
        </p>

        {/* ---------- Section 1: Sign Census + DBIT Backfill ---------- */}
        <div className="flex gap-2 flex-wrap">
          <Button onClick={runCensus} disabled={loading} size="sm" data-testid="run-sign-census-btn">
            {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ArrowDownUp className="h-4 w-4 mr-1" />}
            Run Census
          </Button>
          <Button onClick={runPreview} disabled={previewLoading} variant="outline" size="sm" data-testid="preview-sign-backfill-btn">
            {previewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview Backfill
          </Button>
          {preview && preview.total_to_flip > 0 && (
            <Button onClick={runApply} disabled={applying} variant="destructive" size="sm" data-testid="apply-sign-backfill-btn">
              {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
              Apply ({preview.total_to_flip})
            </Button>
          )}
        </div>

        {/* Census results */}
        {census && (
          <div className="space-y-3">
            <div className="text-sm font-medium">Provider Summary</div>
            <div className="overflow-x-auto">
              <table className="text-xs w-full" data-testid="sign-census-provider-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-1">Provider</th>
                    <th className="p-1 text-right">Total</th>
                    <th className="p-1 text-right">Positive</th>
                    <th className="p-1 text-right">Negative</th>
                    <th className="p-1 text-right">Zero</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(census.by_provider).map(([source, counts]) => (
                    <tr key={source} className="border-b border-border/50">
                      <td className="p-1 font-mono">{source}</td>
                      <td className="p-1 text-right">{counts.total}</td>
                      <td className="p-1 text-right text-green-600">{counts.positive}</td>
                      <td className="p-1 text-right text-red-600">{counts.negative}</td>
                      <td className="p-1 text-right text-muted-foreground">{counts.zero}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {census.debit_shaped_positive_total > 0 && (
              <>
                <div className="flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <span className="text-sm font-medium text-amber-600">
                    {census.debit_shaped_positive_total} debit-shaped but positive
                  </span>
                </div>

                {census.samples.length > 0 && (
                  <div className="overflow-x-auto">
                    <div className="text-xs font-medium mb-1">Sample debit-shaped positive transactions</div>
                    <table className="text-xs w-full" data-testid="sign-census-samples-table">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="p-1">Source</th>
                          <th className="p-1">Account</th>
                          <th className="p-1">Date</th>
                          <th className="p-1">Description</th>
                          <th className="p-1 text-right">Amount</th>
                          <th className="p-1">Indicator</th>
                        </tr>
                      </thead>
                      <tbody>
                        {census.samples.map((s, i) => (
                          <tr key={i} className="border-b border-border/50">
                            <td className="p-1 font-mono">{s.source}</td>
                            <td className="p-1 font-mono truncate max-w-[120px]">{s.bank_account_id}</td>
                            <td className="p-1 whitespace-nowrap">{(s.date || '').slice(0, 10)}</td>
                            <td className="p-1 truncate max-w-[200px]">{s.description}</td>
                            <td className="p-1 text-right font-mono text-green-600">
                              {fmt(s.amount)} {s.currency}
                            </td>
                            <td className="p-1">
                              {s.eb_raw_fields?.credit_debit_indicator ? (
                                <Badge variant="outline" className="text-xs">
                                  {s.eb_raw_fields.credit_debit_indicator}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">n/a</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {census.debit_shaped_positive_total === 0 && (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm text-green-600">
                  No debit-shaped-but-positive transactions detected
                </span>
              </div>
            )}

            <div className="text-xs text-muted-foreground">
              EB indicator coverage: {census.indicator_coverage.eb_with_indicator} / {census.indicator_coverage.eb_total}{' '}
              ({census.indicator_coverage.coverage_pct}%)
            </div>

            {/* Bridge direction-recovery summary */}
            {census.bridge_direction_recovery?.total > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="bridge-direction-summary">
                Bridge: {census.bridge_direction_recovery.total} txs, no stored direction field.{' '}
                {census.bridge_direction_recovery.accounts.filter(a => a.all_positive).length > 0 && (
                  <span className="text-amber-600">
                    {census.bridge_direction_recovery.accounts.filter(a => a.all_positive).length} account(s) all-positive.
                  </span>
                )}{' '}
                Recovery: re-sync from Bridge API.
              </div>
            )}

            {/* OB direction-recovery summary */}
            {census.ob_direction_recovery?.total > 0 && (
              <div className="text-xs text-muted-foreground" data-testid="ob-direction-summary">
                OB transaction_type coverage: {census.ob_direction_recovery.with_transaction_type} / {census.ob_direction_recovery.total}{' '}
                ({census.ob_direction_recovery.coverage_pct}%).{' '}
                {census.ob_direction_recovery.accounts.filter(a => a.all_positive).length > 0 && (
                  <span className="text-amber-600">
                    {census.ob_direction_recovery.accounts.filter(a => a.all_positive).length} account(s) all-positive.
                  </span>
                )}{' '}
                Recovery: re-sync from TrueLayer.
              </div>
            )}
          </div>
        )}

        {/* Preview results */}
        {preview && (
          <div className="space-y-2 border-t pt-3">
            <div className="text-sm font-medium">Backfill Preview</div>
            {preview.total_to_flip === 0 ? (
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <span className="text-sm">
                  No transactions need repair
                  {preview.already_repaired > 0 && ` (${preview.already_repaired} already repaired)`}
                </span>
              </div>
            ) : (
              <>
                <div className="text-sm">
                  <strong>{preview.total_to_flip}</strong> DBIT transactions stored positive would be flipped.
                  {preview.already_repaired > 0 && (
                    <span className="text-muted-foreground"> ({preview.already_repaired} already repaired)</span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="text-xs w-full">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-1">Account</th>
                        <th className="p-1 text-right">Flip Count</th>
                        <th className="p-1 text-right">Sum Before</th>
                        <th className="p-1 text-right">Sum After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.per_account.map((row, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="p-1 font-mono truncate max-w-[200px]">{row.bank_account_id}</td>
                          <td className="p-1 text-right">{row.flip_count}</td>
                          <td className="p-1 text-right font-mono text-green-600">{fmt(row.sum_before_flip)}</td>
                          <td className="p-1 text-right font-mono text-red-600">{fmt(row.sum_after_flip)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        )}

        <JobErrorBlock error={applyError} testId="apply-sign-backfill-error" />

        {/* Apply result */}
        {applyResult && (
          <div className="space-y-1 border-t pt-3" data-testid="apply-sign-backfill-result">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-sm font-medium">
                Repaired {applyResult.total_flipped} transaction(s) across {applyResult.accounts_affected} account(s)
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              Re-run census to verify debit-shaped-positive count dropped to ~0.
              Then check SCI 113 Bank Transactions page for correct signs.
            </p>
          </div>
        )}

        {/* ---------- Section 2: No-Indicator Re-sync ---------- */}
        <div className="border-t pt-4 space-y-3">
          <div className="text-sm font-medium flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            No-Indicator Re-sync (PSD2)
          </div>
          <p className="text-xs text-muted-foreground">
            Transactions without credit_debit_indicator (pre-raw-fields era). Re-fetches
            from Enable Banking via PSD2 history window (90 days) to obtain indicator and
            correct the sign.
          </p>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={runResyncCensus} disabled={resyncCensusLoading} size="sm" variant="outline" data-testid="resync-census-btn">
              {resyncCensusLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
              Re-sync Census
            </Button>
            <Button onClick={runResyncPreview} disabled={resyncPreviewLoading} size="sm" variant="outline" data-testid="resync-preview-btn">
              {resyncPreviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
              Re-sync Preview
            </Button>
            {resyncPreview && resyncPreview.total_resyncable_estimate > 0 && (
              <Button onClick={runResyncApply} disabled={resyncApplying} variant="destructive" size="sm" data-testid="resync-apply-btn">
                {resyncApplying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                Apply Re-sync (~{resyncPreview.total_resyncable_estimate})
              </Button>
            )}
          </div>

          {/* Re-sync census results */}
          {resyncCensus && (
            <div className="space-y-2">
              {resyncCensus.total_no_indicator === 0 ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm text-green-600">All EB transactions have an indicator</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span className="text-sm text-amber-600">
                      {resyncCensus.total_no_indicator} without indicator
                      ({resyncCensus.total_no_indicator_positive} positive)
                    </span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="text-xs w-full" data-testid="resync-census-table">
                      <thead>
                        <tr className="border-b text-left">
                          <th className="p-1">Account</th>
                          <th className="p-1 text-right">Total</th>
                          <th className="p-1 text-right">With Ind.</th>
                          <th className="p-1 text-right">No Ind.</th>
                          <th className="p-1 text-right">No-Ind +ve</th>
                          <th className="p-1">Date Range</th>
                          <th className="p-1">Era</th>
                        </tr>
                      </thead>
                      <tbody>
                        {resyncCensus.accounts
                          .filter(a => a.without_indicator > 0)
                          .map((a, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="p-1 font-mono truncate max-w-[140px]">{a.bank_account_id}</td>
                              <td className="p-1 text-right">{a.total}</td>
                              <td className="p-1 text-right text-green-600">{a.with_indicator}</td>
                              <td className="p-1 text-right text-amber-600">{a.without_indicator}</td>
                              <td className="p-1 text-right text-red-600">{a.no_indicator_positive}</td>
                              <td className="p-1 whitespace-nowrap text-muted-foreground">
                                {a.no_indicator_date_range
                                  ? `${(a.no_indicator_date_range.min || '').slice(0, 10)} - ${(a.no_indicator_date_range.max || '').slice(0, 10)}`
                                  : '-'}
                              </td>
                              <td className="p-1">
                                {a.no_indicator_missing_raw_fields > 0 ? (
                                  <Badge variant="outline" className="text-xs">pre-raw</Badge>
                                ) : (
                                  <Badge variant="secondary" className="text-xs">has-raw</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground italic">
                    {resyncCensus.diagnosis}
                  </p>
                </>
              )}
            </div>
          )}

          {/* Re-sync preview results */}
          {resyncPreview && (
            <div className="space-y-2 border-t pt-2">
              <div className="text-sm font-medium">Re-sync Feasibility</div>
              <div className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground text-xs block">Resyncable</span>
                  <span className="font-mono">{resyncPreview.total_resyncable_estimate ?? '?'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs block">Residual</span>
                  <span className="font-mono text-amber-600">{resyncPreview.total_residual_estimate ?? '?'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs block">PSD2 cutoff</span>
                  <span className="font-mono text-xs">{resyncPreview.psd2_cutoff_date}</span>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="text-xs w-full" data-testid="resync-preview-table">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="p-1">Account</th>
                      <th className="p-1 text-right">No Ind.</th>
                      <th className="p-1 text-right">+ve</th>
                      <th className="p-1 text-right">Sum +ve</th>
                      <th className="p-1">Dates</th>
                      <th className="p-1">PSD2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resyncPreview.accounts.map((a, i) => (
                      <tr key={i} className="border-b border-border/50">
                        <td className="p-1 font-mono truncate max-w-[140px]">
                          {a.account_name || a.bank_account_id}
                        </td>
                        <td className="p-1 text-right">{a.no_indicator_count}</td>
                        <td className="p-1 text-right text-red-600">{a.positive_count}</td>
                        <td className="p-1 text-right font-mono">{fmt(a.sum_positive)}</td>
                        <td className="p-1 whitespace-nowrap text-xs text-muted-foreground">
                          {(a.date_range.min || '').slice(0, 10)} - {(a.date_range.max || '').slice(0, 10)}
                        </td>
                        <td className="p-1">
                          {a.within_psd2_window ? (
                            <Badge className="text-xs bg-green-100 text-green-800">in window</Badge>
                          ) : a.partially_within ? (
                            <Badge className="text-xs bg-amber-100 text-amber-800">partial</Badge>
                          ) : (
                            <Badge className="text-xs bg-red-100 text-red-800">outside</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <JobErrorBlock error={resyncError} testId="resync-apply-error" />

          {/* Re-sync apply result */}
          {resyncResult && (
            <div className="space-y-1 border-t pt-2">
              {resyncResult.status === 'no_work' ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">{resyncResult.message}</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">
                      Re-synced {resyncResult.total_resynced}, {resyncResult.total_sign_flipped} sign(s) changed
                    </span>
                  </div>
                  {resyncResult.total_unmatched_residual > 0 && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        {resyncResult.total_unmatched_residual} unmatched residual (outside PSD2 window)
                      </span>
                    </div>
                  )}
                  {resyncResult.per_account.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="p-1">Account</th>
                            <th className="p-1 text-right">Fetched</th>
                            <th className="p-1 text-right">Matched</th>
                            <th className="p-1 text-right">Sign Changed</th>
                            <th className="p-1 text-right">Unmatched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {resyncResult.per_account.map((r, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="p-1 font-mono truncate max-w-[140px]">{r.bank_account_id}</td>
                              <td className="p-1 text-right">{r.fetched_from_provider ?? '-'}</td>
                              <td className="p-1 text-right text-green-600">{r.matched_and_updated ?? '-'}</td>
                              <td className="p-1 text-right text-amber-600">{r.sign_changed ?? '-'}</td>
                              <td className="p-1 text-right text-red-600">{r.unmatched_residual ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {resyncResult.note}
                  </p>
                </>
              )}
            </div>
          )}
        </div>

        {/* ---------- Section 3: Bridge / OB Re-sync ---------- */}
        <div className="border-t pt-4 space-y-3">
          <div className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4" />
            Bridge / OpenBanking Re-sync
          </div>
          <p className="text-xs text-muted-foreground">
            Bridge and TrueLayer transactions without a stored direction field. Re-fetches from
            the provider API to recover correct signed amounts.
          </p>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={runBridgePreview} disabled={bridgePreviewLoading} size="sm" variant="outline" data-testid="bridge-resync-preview-btn">
              {bridgePreviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
              Bridge Preview
            </Button>
            {bridgePreview && bridgePreview.total_not_resynced > 0 && (
              <Button onClick={runBridgeApply} disabled={bridgeApplying} variant="destructive" size="sm" data-testid="bridge-resync-apply-btn">
                {bridgeApplying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                Bridge Apply ({bridgePreview.total_not_resynced})
              </Button>
            )}
            <Button onClick={runObPreview} disabled={obPreviewLoading} size="sm" variant="outline" data-testid="ob-resync-preview-btn">
              {obPreviewLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
              OB Preview
            </Button>
            {obPreview && obPreview.total_without_type > 0 && (
              <Button onClick={runObApply} disabled={obApplying} variant="destructive" size="sm" data-testid="ob-resync-apply-btn">
                {obApplying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
                OB Apply ({obPreview.total_without_type})
              </Button>
            )}
          </div>

          {/* Bridge preview results */}
          {bridgePreview && (
            <div className="space-y-2">
              <div className="text-sm font-medium">Bridge Re-sync Preview</div>
              {bridgePreview.total_not_resynced === 0 ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">All Bridge transactions resynced
                    {bridgePreview.already_resynced > 0 && ` (${bridgePreview.already_resynced} already resynced)`}
                  </span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="text-xs w-full" data-testid="bridge-resync-preview-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-1">Account</th>
                        <th className="p-1 text-right">Not Resynced</th>
                        <th className="p-1 text-right">+ve</th>
                        <th className="p-1 text-right">Sum +ve</th>
                        <th className="p-1">Dates</th>
                        <th className="p-1">Connection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bridgePreview.accounts.map((a, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="p-1 font-mono truncate max-w-[140px]">{a.account_name || a.bank_account_id}</td>
                          <td className="p-1 text-right">{a.not_resynced_count}</td>
                          <td className="p-1 text-right text-amber-600">{a.positive_count}</td>
                          <td className="p-1 text-right font-mono">{fmt(a.sum_positive)}</td>
                          <td className="p-1 whitespace-nowrap text-xs text-muted-foreground">
                            {(a.date_range?.min || '').slice(0, 10)} - {(a.date_range?.max || '').slice(0, 10)}
                          </td>
                          <td className="p-1">
                            {a.connection_active ? (
                              <Badge className="text-xs bg-green-100 text-green-800">active</Badge>
                            ) : (
                              <Badge className="text-xs bg-red-100 text-red-800">inactive</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <JobErrorBlock error={bridgeError} testId="bridge-resync-apply-error" />

          {/* Bridge apply result */}
          {bridgeResult && (
            <div className="space-y-1 border-t pt-2" data-testid="bridge-resync-apply-result">
              {bridgeResult.status === 'no_work' ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">{bridgeResult.message}</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">
                      Bridge: {bridgeResult.total_resynced} resynced, {bridgeResult.total_sign_changed} sign(s) changed
                    </span>
                  </div>
                  {bridgeResult.total_sign_changed === 0 && bridgeResult.total_resynced > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Zero sign changes confirms these accounts genuinely receive only credits.
                    </p>
                  )}
                  {bridgeResult.total_unmatched_residual > 0 && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        {bridgeResult.total_unmatched_residual} unmatched residual
                      </span>
                    </div>
                  )}
                  {bridgeResult.per_account?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="p-1">Account</th>
                            <th className="p-1 text-right">Fetched</th>
                            <th className="p-1 text-right">Resynced</th>
                            <th className="p-1 text-right">Sign Changed</th>
                            <th className="p-1 text-right">Unmatched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bridgeResult.per_account.map((r, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="p-1 font-mono truncate max-w-[140px]">{r.bank_account_id}</td>
                              <td className="p-1 text-right">{r.fetched_from_provider ?? '-'}</td>
                              <td className="p-1 text-right text-green-600">{r.matched_and_resynced ?? '-'}</td>
                              <td className="p-1 text-right text-amber-600">{r.sign_changed ?? '-'}</td>
                              <td className="p-1 text-right text-red-600">{r.unmatched_residual ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{bridgeResult.note}</p>
                </>
              )}
            </div>
          )}

          {/* OB preview results */}
          {obPreview && (
            <div className="space-y-2 border-t pt-2">
              <div className="text-sm font-medium">OB (TrueLayer) Re-sync Preview</div>
              {obPreview.total_without_type === 0 ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">All OB transactions have transaction_type
                    {obPreview.already_resynced > 0 && ` (${obPreview.already_resynced} already resynced)`}
                  </span>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="text-xs w-full" data-testid="ob-resync-preview-table">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="p-1">Account</th>
                        <th className="p-1">Kind</th>
                        <th className="p-1 text-right">Needs Fix</th>
                        <th className="p-1 text-right">+ve</th>
                        <th className="p-1 text-right">Sum +ve</th>
                        <th className="p-1">Type Dist</th>
                        <th className="p-1">Raw Sign</th>
                        <th className="p-1 text-right">Exp. Changed</th>
                        <th className="p-1">Dates</th>
                        <th className="p-1">Connection</th>
                      </tr>
                    </thead>
                    <tbody>
                      {obPreview.accounts.map((a, i) => (
                        <tr key={i} className="border-b border-border/50">
                          <td className="p-1 font-mono truncate max-w-[140px]">{a.account_name || a.bank_account_id}</td>
                          <td className="p-1" data-testid="ob-preview-account-kind">
                            <Badge className={`text-xs ${a.account_kind === 'card' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                              {a.account_kind || 'unknown'}
                            </Badge>
                          </td>
                          <td className="p-1 text-right">{a.without_type_count}</td>
                          <td className="p-1 text-right text-amber-600">{a.positive_count}</td>
                          <td className="p-1 text-right font-mono">{fmt(a.sum_positive)}</td>
                          <td className="p-1 text-xs" data-testid="ob-preview-type-dist">
                            {a.type_distribution && Object.keys(a.type_distribution).length > 0 ? (
                              Object.entries(a.type_distribution).map(([k, v]) => (
                                <span key={k} className="mr-1">{k}:{v}</span>
                              ))
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="p-1 text-xs whitespace-nowrap" data-testid="ob-preview-raw-sign">
                            {a.raw_sign_distribution ? (
                              <>+{a.raw_sign_distribution.positive} / -{a.raw_sign_distribution.negative} / 0:{a.raw_sign_distribution.zero}</>
                            ) : '-'}
                          </td>
                          <td className="p-1 text-right text-amber-600" data-testid="ob-preview-expected-changed">{a.expected_sign_changed ?? '-'}</td>
                          <td className="p-1 whitespace-nowrap text-xs text-muted-foreground">
                            {(a.date_range?.min || '').slice(0, 10)} - {(a.date_range?.max || '').slice(0, 10)}
                          </td>
                          <td className="p-1">
                            {a.connection_active ? (
                              <Badge className="text-xs bg-green-100 text-green-800">active</Badge>
                            ) : (
                              <Badge className="text-xs bg-red-100 text-red-800">inactive</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {/* Unmatched detail rows */}
                  {obPreview.accounts.some(a => a.unmatched_detail?.length > 0) && (
                    <div className="mt-2" data-testid="ob-preview-unmatched-detail">
                      <div className="text-xs font-medium text-amber-600 mb-1">Unmatched Residual Detail</div>
                      <table className="text-xs w-full">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="p-1">TX ID</th>
                            <th className="p-1">Date</th>
                            <th className="p-1 text-right">Amount</th>
                            <th className="p-1">Description</th>
                          </tr>
                        </thead>
                        <tbody>
                          {obPreview.accounts.flatMap(a => (a.unmatched_detail || []).map((d, j) => (
                            <tr key={`${a.bank_account_id}_${j}`} className="border-b border-border/50">
                              <td className="p-1 font-mono truncate max-w-[180px]">{d.id}</td>
                              <td className="p-1 whitespace-nowrap">{d.date}</td>
                              <td className="p-1 text-right font-mono">{d.amount}</td>
                              <td className="p-1 truncate max-w-[200px]">{d.description}</td>
                            </tr>
                          )))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <JobErrorBlock error={obError} testId="ob-resync-apply-error" />

          {/* OB apply result */}
          {obResult && (
            <div className="space-y-1 border-t pt-2">
              {obResult.status === 'no_work' ? (
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                  <span className="text-sm">{obResult.message}</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium">
                      OB: {obResult.total_resynced} resynced, {obResult.total_sign_changed} sign(s) changed
                    </span>
                  </div>
                  {obResult.total_unmatched_residual > 0 && (
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <span className="text-xs text-amber-600">
                        {obResult.total_unmatched_residual} unmatched residual
                      </span>
                    </div>
                  )}
                  {obResult.per_account?.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="text-xs w-full" data-testid="ob-resync-apply-table">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="p-1">Account</th>
                            <th className="p-1">Kind</th>
                            <th className="p-1 text-right">Fetched</th>
                            <th className="p-1 text-right">Resynced</th>
                            <th className="p-1 text-right">Sign Changed</th>
                            <th className="p-1 text-right">Unmatched</th>
                          </tr>
                        </thead>
                        <tbody>
                          {obResult.per_account.map((r, i) => (
                            <tr key={i} className="border-b border-border/50">
                              <td className="p-1 font-mono truncate max-w-[140px]">{r.bank_account_id}</td>
                              <td className="p-1">
                                <Badge className={`text-xs ${r.account_kind === 'card' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                                  {r.account_kind || 'bank'}
                                </Badge>
                              </td>
                              <td className="p-1 text-right">{r.fetched_from_provider ?? '-'}</td>
                              <td className="p-1 text-right text-green-600">{r.matched_and_resynced ?? '-'}</td>
                              <td className="p-1 text-right text-amber-600">{r.sign_changed ?? '-'}</td>
                              <td className="p-1 text-right text-red-600">{r.unmatched_residual ?? '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {/* Unmatched detail for apply results */}
                      {obResult.per_account.some(r => r.unmatched_detail?.length > 0) && (
                        <div className="mt-2" data-testid="ob-apply-unmatched-detail">
                          <div className="text-xs font-medium text-amber-600 mb-1">Unmatched Residual Detail</div>
                          <table className="text-xs w-full">
                            <thead>
                              <tr className="border-b text-left">
                                <th className="p-1">TX ID</th>
                                <th className="p-1">Date</th>
                                <th className="p-1 text-right">Amount</th>
                                <th className="p-1">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {obResult.per_account.flatMap(r => (r.unmatched_detail || []).map((d, j) => (
                                <tr key={`${r.bank_account_id}_${j}`} className="border-b border-border/50">
                                  <td className="p-1 font-mono truncate max-w-[180px]">{d.id}</td>
                                  <td className="p-1 whitespace-nowrap">{d.date}</td>
                                  <td className="p-1 text-right font-mono">{d.amount}</td>
                                  <td className="p-1 truncate max-w-[200px]">{d.description}</td>
                                </tr>
                              )))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">{obResult.note}</p>
                </>
              )}
            </div>
          )}
        </div>

        {/* ---------- Section 4: Amount BSON Type Census ---------- */}
        <div className="border-t pt-4 space-y-3">
          <div className="text-sm font-medium flex items-center gap-2">
            <Database className="h-4 w-4" />
            Amount BSON Type Census
          </div>
          <p className="text-xs text-muted-foreground">
            Per-provider MongoDB $type breakdown of the stored `amount` field -- confirms which
            providers need $toDecimal coercion in aggregation pipelines.
          </p>

          <Button onClick={runAmountTypes} disabled={amountTypesLoading} size="sm" variant="outline" data-testid="amount-types-census-btn">
            {amountTypesLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Database className="h-4 w-4 mr-1" />}
            Run Amount Type Census
          </Button>

          {amountTypes && (
            <div className="overflow-x-auto">
              <table className="text-xs w-full" data-testid="amount-types-census-table">
                <thead>
                  <tr className="border-b text-left">
                    <th className="p-1">Source</th>
                    <th className="p-1">BSON Type</th>
                    <th className="p-1 text-right">Count</th>
                    <th className="p-1">Sample Values</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(amountTypes.by_provider || {}).flatMap(([source, info]) =>
                    info.types.map((t, i) => (
                      <tr key={`${source}-${t.bson_type}-${i}`} className="border-b border-border/50">
                        <td className="p-1 font-mono">{source}</td>
                        <td className="p-1">
                          <Badge variant={t.bson_type === 'string' ? 'secondary' : 'outline'} className="text-xs">
                            {t.bson_type}
                          </Badge>
                        </td>
                        <td className="p-1 text-right">{t.count}</td>
                        <td className="p-1 font-mono text-muted-foreground truncate max-w-[240px]">
                          {(t.sample_values || []).join(', ')}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
